import { ForwardedRef, ReactNode, forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import { Option } from "capitalrx-components-2.0/lib/forms/Dropdown/Option";
import { SharedDropdown } from "capitalrx-components-2.0/lib/forms/Dropdown/SharedDropdown";
import { useInputDisabled, useInputReadonly } from "capitalrx-components-2.0/lib/forms/InputGroup";
import { useSubmitting } from "capitalrx-components-2.0/lib/forms/SubmitGroup";
import telephoneTextboxStyles from "capitalrx-components-2.0/lib/forms/TelephoneTextbox/styles.module.scss";
import { Placeholder } from "capitalrx-components-2.0/lib/forms/Textbox/Placeholder";
import { StyledNativeInput } from "capitalrx-components-2.0/lib/forms/Textbox/StyledNativeInput";
import { useValidity } from "capitalrx-components-2.0/lib/forms/ValidityGroup";
import { InputContainer } from "capitalrx-components-2.0/lib/forms/shared";
import { useSimpleInputState } from "capitalrx-components-2.0/lib/forms/useSimpleInputState";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general/LoadingGroup";
import { useId } from "capitalrx-components-2.0/lib/hooks/useId";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { getBasePropsFromHtmlProps } from "capitalrx-components-2.0/lib/utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import classNames from "classnames";
import { CountryCode, getCountries, getCountryCallingCode } from "libphonenumber-js";

import { FieldProps } from "./Field";
import { TelephoneTextboxProps } from "./TelephoneTextbox";
import { errorMessages } from "./errorMessages";
import { usePhoneInput } from "./usePhoneInput";
import { countryFlagEmoji, formatPhoneNumber, getMaxPossibleDigitsForCountry, getPhoneNumberValue, isCountryCode } from "./utils";

// Navigation and special keys allowed in the input
const ALLOWED_KEYS: ReadonlyArray<string> = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete", "Home", "End", "Enter"];

/**
 * Country option for the country selector dropdown
 */
interface CountryOption {
  code: CountryCode;
  label: ReactNode;
}

/**
 * The `Input` component properties.
 */
export interface InputProps extends TelephoneTextboxProps, Pick<FieldProps, "required"> {}

/**
 * An input field component which allows entry of a Telephone value.
 */
export const Input = forwardRef(function Input(props: InputProps, ref: ForwardedRef<HTMLInputElement>) {
  const {
    label,
    required = false,
    tooltip,
    instructions,
    hideLabel,
    defaultValue,
    autoFocus,
    tabIndex,
    onChange,
    countryCode,
    countryOptions,
    onCountryChange,
  } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [telephoneTextboxStyles.TelephoneTextbox],
    testIdPrefix: "TelephoneTextbox",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();
  const [inputValue, setInputValue] = useSimpleInputState(defaultValue);

  const [internalCountry, setInternalCountry] = useState<CountryCode | undefined>(countryCode);
  // Treat the component as controlled only when both `countryCode` and `onCountryChange` are provided.
  const isControlled = countryCode !== undefined && typeof onCountryChange === "function";

  useEffect(() => {
    // Keep internalCountry initialized/synced from the prop so uncontrolled mode can start from the provided value.
    if (countryCode !== undefined) {
      setInternalCountry(countryCode);
    }
  }, [countryCode]);

  const effectiveCountry = useMemo(() => (isControlled ? countryCode : internalCountry), [isControlled, countryCode, internalCountry]);

  // Memoized max digits per country to avoid recomputing on each keydown
  const countryMaxDigits = useMemo(() => getMaxPossibleDigitsForCountry(effectiveCountry), [effectiveCountry]);

  // Reformat display when the country changes, keeping the underlying value stable
  useEffect(() => {
    if (!inputValue) return;
    const currentE164 = getPhoneNumberValue(inputValue);
    const reformatted = formatPhoneNumber(currentE164, effectiveCountry);
    if (reformatted !== inputValue) {
      setInputValue(reformatted, inputValue ?? "");
    }
  }, [effectiveCountry]);

  const { applyPhoneInputValue } = usePhoneInput({
    context: { effectiveCountry, isCountryControlled: isControlled },
    prevDisplay: inputValue ?? "",
    setDisplay: (next, prev) => setInputValue(next, prev),
    onChange: (next, prev) => onChange?.(next, prev),
    onCountryDetected: (detected) => {
      if (isControlled) {
        onCountryChange?.(detected);
      } else {
        setInternalCountry(detected);
        onCountryChange?.(detected);
      }
    },
  });

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData("Text");
      if (!pastedText.trim()) {
        e.preventDefault();
        if (process.env.NODE_ENV !== "production") console.log(errorMessages["invalid paste"]);
        return;
      }
      applyPhoneInputValue(pastedText);
      e.preventDefault();
    },
    [applyPhoneInputValue],
  );

  const handlesKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.ctrlKey || e.metaKey || (ALLOWED_KEYS.includes(e.key) && e.key !== "Enter")) {
        return;
      }

      // Helper to compute digits count excluding DSN prefix and non-digits
      const countDigits = (v: string) => v.replace(/^\d{2}\+/, "").replace(/\D/g, "").length;
      const value = inputValue ?? "";
      const posStart = e.currentTarget.selectionStart ?? value.length;
      const posEnd = e.currentTarget.selectionEnd ?? posStart;

      // Allow '+' at start (international) and at position 2 for DSN like '94+'
      if (e.key === "+" && !value.includes("+")) {
        // Allow immediately after two-digit DSN prefix
        if (posStart === 0 || (posStart === 2 && /^\d{2}/.test(value))) {
          return;
        }
        e.preventDefault();
        return;
      }

      // Allow digits
      const isDigit = /^\d$/.test(e.key);
      if (isDigit) {
        // Simulate the next value after inserting this digit at the cursor/selection
        const nextValue = value.slice(0, posStart) + e.key + value.slice(posEnd);

        // Skip country-specific digit limit when user types international format (starts with +)
        const startsWithPlus = nextValue.trim().startsWith("+");
        if (!startsWithPlus) {
          const nextDigits = countDigits(nextValue);
          if (nextDigits > countryMaxDigits) {
            e.preventDefault();
            return;
          }
        }
        return;
      }

      // Block everything else
      e.preventDefault();
    },
    [inputValue],
  );

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      applyPhoneInputValue(e.target.value);
    },
    [applyPhoneInputValue],
  );

  const countryList: CountryOption[] = useMemo(() => {
    if (countryOptions?.length) return countryOptions;

    const displayNames = new Intl.DisplayNames(["en"], { type: "region" });

    const countries = getCountries()
      .map<CountryOption>((code) => ({
        code,
        label: (
          <span className={telephoneTextboxStyles.countryOptionLabel}>
            <span className={telephoneTextboxStyles.countryFlag}>{countryFlagEmoji(code)}</span>
            <span className={telephoneTextboxStyles.countryNameAndCode}>
              {displayNames.of(code) || code} +{getCountryCallingCode(code)}
            </span>
          </span>
        ),
      }))
      .sort((a, b) => {
        const nameA = displayNames.of(a.code) || a.code;
        const nameB = displayNames.of(b.code) || b.code;
        return nameA.localeCompare(nameB);
      });

    return countries;
  }, [countryOptions]);

  const handleCountryChange = useCallback(
    (country: string | number | null) => {
      if (typeof country === "string" && isCountryCode(country)) {
        if (onCountryChange) {
          onCountryChange(country);
        } else {
          setInternalCountry(country);
        }
      }
    },
    [onCountryChange],
  );

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder hasInstructions={!!instructions} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={instructions}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideLabel={hideLabel}
      >
        <Body className={classNames(telephoneTextboxStyles.TelephoneTextboxBody, { [telephoneTextboxStyles.invalid]: invalid })}>
          <Shelf spacing="none">
            {countryList.length > 0 ? (
              <SharedDropdown
                className={telephoneTextboxStyles.countrySelector}
                id={`${id}-country-selector`}
                aria-label="Select country"
                disabled={disabled || readOnly || submitting}
                value={effectiveCountry ?? "US"}
                onChange={handleCountryChange}
                searchable
              >
                {countryList.map((country) => (
                  <Option key={country.code} value={country.code}>
                    {country.label}
                  </Option>
                ))}
              </SharedDropdown>
            ) : null}
            <StyledNativeInput
              id={id}
              aria-labelledby={`${id}-label`}
              aria-invalid={invalid}
              aria-required={required}
              className={classNames("input", { invalid })}
              type="tel"
              inputMode="tel"
              value={inputValue ?? ""}
              onChange={handleOnChange}
              onKeyDown={handlesKeyDown}
              onPaste={handlePaste}
              disabled={disabled}
              autoFocus={autoFocus}
              readOnly={readOnly || submitting}
              tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
              ref={ref}
            />
          </Shelf>
        </Body>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
